import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { BuilderComponentType } from '../interfaces/builder-screen.models';

export interface PropertyPreset {
  id: string;
  name: string;
  description: string;
  componentTypes: BuilderComponentType[];
  properties: Record<string, any>;
  tags: string[];
  popularity: number;
  isCustom: boolean;
  createdAt: Date;
  icon?: string;
  previewImage?: string;
}

export interface PresetCategory {
  id: string;
  name: string;
  icon: string;
  presets: PropertyPreset[];
}

@Injectable({
  providedIn: 'root',
})
export class PropertyPresetsService {
  private presetsSubject = new BehaviorSubject<PropertyPreset[]>(this.getBuiltInPresets());
  
  presets$ = this.presetsSubject.asObservable();
  
  // Obtém presets para tipo de componente específico
  getPresetsForComponent(componentType: BuilderComponentType): Observable<PropertyPreset[]> {
    return this.presets$.pipe(
      map(presets => presets.filter(preset => 
        preset.componentTypes.includes(componentType) || 
        preset.componentTypes.includes('all' as BuilderComponentType)
      ).sort((a, b) => b.popularity - a.popularity))
    );
  }

  // Obtém categorias de preset
  getPresetCategories(componentType: BuilderComponentType): Observable<PresetCategory[]> {
    return this.getPresetsForComponent(componentType).pipe(
      map(presets => this.groupPresetsByCategory(presets))
    );
  }

  // Aplica preset ao componente
  applyPreset(presetId: string): PropertyPreset | null {
    const preset = this.presetsSubject.value.find(p => p.id === presetId);
    if (preset) {
      // Incrementa popularidade
      preset.popularity += 1;
      this.saveCustomPresets();
      return preset;
    }
    return null;
  }

  // Cria preset customizado das propriedades atuais do componente
  createCustomPreset(
    name: string,
    description: string,
    componentType: BuilderComponentType,
    properties: Record<string, any>,
    tags: string[] = []
  ): PropertyPreset {
    const preset: PropertyPreset = {
      id: `custom-${Date.now()}`,
      name,
      description,
      componentTypes: [componentType],
      properties: { ...properties },
      tags: ['custom', ...tags],
      popularity: 0,
      isCustom: true,
      createdAt: new Date(),
    };

    const currentPresets = this.presetsSubject.value;
    this.presetsSubject.next([...currentPresets, preset]);
    this.saveCustomPresets();

    return preset;
  }

  // Remove preset customizado
  deletePreset(presetId: string): boolean {
    const currentPresets = this.presetsSubject.value;
    const preset = currentPresets.find(p => p.id === presetId);
    
    if (preset && preset.isCustom) {
      const updatedPresets = currentPresets.filter(p => p.id !== presetId);
      this.presetsSubject.next(updatedPresets);
      this.saveCustomPresets();
      return true;
    }
    
    return false;
  }

  // Pesquisa presets
  searchPresets(query: string, componentType?: BuilderComponentType): Observable<PropertyPreset[]> {
    const searchTerm = query.toLowerCase();
    
    return this.presets$.pipe(
      map(presets => presets.filter(preset => {
        const matchesSearch = 
          preset.name.toLowerCase().includes(searchTerm) ||
          preset.description.toLowerCase().includes(searchTerm) ||
          preset.tags.some(tag => tag.toLowerCase().includes(searchTerm));
        
        const matchesComponent = !componentType || 
          preset.componentTypes.includes(componentType) ||
          preset.componentTypes.includes('all' as BuilderComponentType);
        
        return matchesSearch && matchesComponent;
      }))
    );
  }

  // Obtém presets populares
  getPopularPresets(componentType: BuilderComponentType, limit: number = 5): Observable<PropertyPreset[]> {
    return this.getPresetsForComponent(componentType).pipe(
      map(presets => presets
        .filter(p => p.popularity > 0)
        .sort((a, b) => b.popularity - a.popularity)
        .slice(0, limit)
      )
    );
  }

  // Obtém presets recentes
  getRecentPresets(componentType: BuilderComponentType, limit: number = 5): Observable<PropertyPreset[]> {
    return this.getPresetsForComponent(componentType).pipe(
      map(presets => presets
        .filter(p => p.isCustom)
        .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
        .slice(0, limit)
      )
    );
  }

  private groupPresetsByCategory(presets: PropertyPreset[]): PresetCategory[] {
    const categories: PresetCategory[] = [
      { id: 'popular', name: 'Popular', icon: 'fire', presets: [] },
      { id: 'recent', name: 'Recent', icon: 'clock-circle', presets: [] },
      { id: 'styling', name: 'Styling', icon: 'bg-colors', presets: [] },
      { id: 'layout', name: 'Layout', icon: 'layout', presets: [] },
      { id: 'custom', name: 'Custom', icon: 'star', presets: [] },
    ];

    presets.forEach(preset => {
      if (preset.popularity > 2) {
        categories.find(c => c.id === 'popular')?.presets.push(preset);
      }
      
      if (preset.isCustom && this.isRecent(preset.createdAt)) {
        categories.find(c => c.id === 'recent')?.presets.push(preset);
      }
      
      if (preset.tags.includes('styling') || preset.tags.includes('colors')) {
        categories.find(c => c.id === 'styling')?.presets.push(preset);
      } else if (preset.tags.includes('layout') || preset.tags.includes('positioning')) {
        categories.find(c => c.id === 'layout')?.presets.push(preset);
      } else if (preset.isCustom) {
        categories.find(c => c.id === 'custom')?.presets.push(preset);
      }
    });

    return categories.filter(category => category.presets.length > 0);
  }

  private isRecent(date: Date): boolean {
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return date > weekAgo;
  }

  private getBuiltInPresets(): PropertyPreset[] {
    return [
      // Presets de Botão
      {
        id: 'button-primary-blue',
        name: 'Primary Blue',
        description: 'Standard primary button with blue background',
        componentTypes: ['button'],
        properties: {
          buttonType: 'primary',
          backgroundColor: '#1890ff',
          textColor: '#ffffff',
          size: 'default',
        },
        tags: ['styling', 'primary', 'blue'],
        popularity: 15,
        isCustom: false,
        createdAt: new Date('2024-01-01'),
      },
      {
        id: 'button-danger-red',
        name: 'Danger Red',
        description: 'Warning/danger button with red styling',
        componentTypes: ['button'],
        properties: {
          buttonType: 'danger',
          backgroundColor: '#ff4d4f',
          textColor: '#ffffff',
          size: 'default',
        },
        tags: ['styling', 'danger', 'red'],
        popularity: 8,
        isCustom: false,
        createdAt: new Date('2024-01-01'),
      },
      {
        id: 'button-large-success',
        name: 'Large Success',
        description: 'Large green success button',
        componentTypes: ['button'],
        properties: {
          buttonType: 'primary',
          backgroundColor: '#52c41a',
          textColor: '#ffffff',
          size: 'large',
        },
        tags: ['styling', 'success', 'large'],
        popularity: 5,
        isCustom: false,
        createdAt: new Date('2024-01-01'),
      },

      // Presets de Input de Texto
      {
        id: 'text-required-field',
        name: 'Required Field',
        description: 'Standard required text input with validation',
        componentTypes: ['text'],
        properties: {
          required: true,
          backgroundColor: '#ffffff',
          textColor: '#262626',
          minLength: 1,
        },
        tags: ['validation', 'required'],
        popularity: 12,
        isCustom: false,
        createdAt: new Date('2024-01-01'),
      },
      {
        id: 'text-email-field',
        name: 'Email Field',
        description: 'Email input with validation pattern',
        componentTypes: ['text'],
        properties: {
          required: true,
          pattern: '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$',
          placeholder: 'Enter your email address',
        },
        tags: ['validation', 'email', 'pattern'],
        popularity: 10,
        isCustom: false,
        createdAt: new Date('2024-01-01'),
      },

      // Presets de Tabela
      {
        id: 'table-striped',
        name: 'Striped Table',
        description: 'Table with alternating row colors',
        componentTypes: ['table', 'simpletable'],
        properties: {
          headerBackgroundColor: '#fafafa',
          rowBackgroundColor: '#ffffff',
          alternateRowColor: '#f5f5f5',
          borderColor: '#e8e8e8',
        },
        tags: ['styling', 'striped', 'table'],
        popularity: 7,
        isCustom: false,
        createdAt: new Date('2024-01-01'),
      },

      // Presets Universais
      {
        id: 'dark-theme',
        name: 'Dark Theme',
        description: 'Dark theme colors for any component',
        componentTypes: ['all' as BuilderComponentType],
        properties: {
          backgroundColor: '#1f1f1f',
          textColor: '#ffffff',
          borderColor: '#434343',
        },
        tags: ['styling', 'dark', 'theme'],
        popularity: 6,
        isCustom: false,
        createdAt: new Date('2024-01-01'),
      },
    ];
  }

  private saveCustomPresets(): void {
    const customPresets = this.presetsSubject.value.filter(p => p.isCustom);
    localStorage.setItem('custom-property-presets', JSON.stringify(customPresets));
  }

  private loadCustomPresets(): PropertyPreset[] {
    const saved = localStorage.getItem('custom-property-presets');
    if (saved) {
      try {
        return JSON.parse(saved).map((preset: any) => ({
          ...preset,
          createdAt: new Date(preset.createdAt),
        }));
      } catch {
        return [];
      }
    }
    return [];
  }

  // Inicializa service com presets salvos
  init(): void {
    const builtIn = this.getBuiltInPresets();
    const custom = this.loadCustomPresets();
    this.presetsSubject.next([...builtIn, ...custom]);
  }
}
